import { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { PinnedMessages } from "../components/PinnedMessages";
import { AttachPicker, PendingFiles, AttachmentList } from "../components/MsgAttachments";

const hhmm = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function AgentMessages() {
  const [data, setData] = useState(null);
  const [ch, setCh] = useState("rh");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = () => http.get("/agent-app/messages").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);
  useEffect(() => { http.post("/agent-app/messages/seen", null, { params: { channel: ch } }).catch(() => {}); }, [ch, data?.find((c) => c.key === ch)?.messages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = data?.find((c) => c.key === ch);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [cur?.messages.length, ch]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setBusy(true); setErr("");
    try { await http.post("/agent-app/messages", { channel: ch, content: text.trim(), attachments: files.map((f) => f.id) }); setText(""); setFiles([]); await load(); }
    catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="agent-messages">
      <h2 className="font-display text-lg font-extrabold mb-1">Messages</h2>
      <p className="text-xs text-slate-500 mb-3">Écrivez directement au service concerné. Vos messages restent privés.</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {(data || []).map((c) => (
          <button key={c.key} data-testid={`agent-msg-channel-${c.key}`} onClick={() => setCh(c.key)}
            className={`relative py-2 rounded-lg text-sm font-bold border ${c.key === ch ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {c.label}{c.unread > 0 && <span data-testid={`agent-msg-dot-${c.key}`} className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-600 ring-2 ring-white" />}
          </button>
        ))}
      </div>
      {!data && <div className="py-10 grid place-items-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      {cur && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <PinnedMessages items={cur.pinned} base="/agent-app/messages" ackUrl={(id) => `/agent-app/messages/${id}/ack`} canPin={false} onChange={load} />
          <div className="max-h-[50vh] overflow-auto p-3 space-y-2" data-testid="agent-msg-list">
            {cur.messages.map((m) => (
              <div key={m.id} className={`flex ${m.author_type === "agent" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.author_type === "agent" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-900"} ${m.pinned_at ? "ring-2 ring-amber-400" : ""}`}>
                  {m.author_type !== "agent" && <div className="text-[10px] font-bold uppercase opacity-70">{m.user_name} · {cur.label}</div>}
                  {m.content && <div className="text-sm whitespace-pre-wrap">{m.content}</div>}
                  <AttachmentList base="/agent-app/messages" items={m.attachments} />
                  <div className={`text-[10px] mt-0.5 ${m.author_type === "agent" ? "text-sky-100" : "text-slate-500"}`}>{hhmm(m.created_at)}</div>
                </div>
              </div>
            ))}
            {cur.messages.length === 0 && <div className="text-center text-sm text-slate-400 py-8">Aucun message avec le service {cur.label}.</div>}
            <div ref={end} />
          </div>
          <PendingFiles files={files} setFiles={setFiles} />
          <form onSubmit={send} className="border-t border-slate-200 p-2 flex gap-2">
            <AttachPicker camera base="/agent-app/messages" files={files} setFiles={setFiles} />
            <input data-testid="agent-msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={`Message au service ${cur.label}…`} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="agent-msg-send" disabled={busy} className="bg-sky-600 text-white rounded-lg px-3 disabled:opacity-60"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
      {err && <div className="mt-2 text-sm text-rose-700">{err}</div>}
    </section>
  );
}
